import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { NodeRowProps } from '../../types';
import { Checkbox, Collapsible } from 'ncoded-component-library';
import FolderIcon from 'icons/Folder.icon';
import useNodeInfo from '../../hooks/useNodeInfo';
import useNodeVariables from '../../hooks/useNodeVariables';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { NODE_COMPONENT } from '../../data';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import FileRowPlaceholder from '../FileRowPlaceholder';
import { useTranslation } from 'react-i18next';
import { getFileCountInFolder, getDataRoomNodeType } from 'utils';

import './FolderNodeCollaps.styles.scss';

const FolderNodeCollaps: React.FC<NodeRowProps> = (props) => {
  const {
    className,
    node,
    refetchFlag,
    checkedFilesIds,
    onChange,
    getChildrenForNode,
    checkIfNodeIsDisabled,
  } = props;

  const createInfoData = useNodeInfo();
  const [levelNodes, setLevelNodes] = useState([]);
  const { popServerError } = useContext(PopNotificationsContext);
  const [isLoading, setIsLoading] = useState(true);
  const isAlreadyFetched = useRef(false);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const classes = classNames('folder-node-collaps', className);

  const { currState, checked, disabled, nodeClass, hasBuyerFiles } =
    useNodeVariables({
      node,
      checkedFilesIds,
      checkIfNodeIsDisabled,
    });

  const fileCount = getFileCountInFolder(node);

  useEffect(() => {
    if (isAlreadyFetched.current || !isOpen) return;

    (async () => {
      try {
        setIsLoading(true);

        const data = await getChildrenForNode(node?._id);

        setLevelNodes(data);
        isAlreadyFetched.current = true;
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [getChildrenForNode, isOpen, levelNodes, node, popServerError]);

  useEffect(() => setIsOpen(false), [refetchFlag]);

  return (
    <div className="wrapp">
      <Collapsible
        className={classes}
        open={isOpen}
        onAnimationEnd={setIsOpen}
        trigger={
          <div className="folder-node-collaps__trigger">
            <div className="file-access-selector__node-info">
              <ChevrondownIcon
                className={classNames('chevron-icon', { rotated: isOpen })}
              />
              <FolderIcon className="fodler-icon-trigger" />
              <span>
                <p className="name">{node.name}</p>
                {createInfoData(node)}
              </span>

              {!!fileCount && <span className="file-count">{fileCount}</span>}
            </div>

            {hasBuyerFiles && <p>{t('onlySellSideFilesWillBeShared')}</p>}
          </div>
        }
      >
        <ul className="file-access-selector__nodes">
          {levelNodes.map((node) => {
            const nodeType = getDataRoomNodeType(node);

            return (
              <li
                key={node._id}
                className={`file-access-selector__${nodeType}`}
              >
                {node &&
                  NODE_COMPONENT[nodeType]({
                    node,
                    refetchFlag,
                    checkedFilesIds,
                    onChange,
                    getChildrenForNode,
                    checkIfNodeIsDisabled,
                  })}
              </li>
            );
          })}
          {isLoading &&
            Array(3)
              .fill(null)
              .map((_, index) => <FileRowPlaceholder key={index} />)}
          {!isLoading && !levelNodes.length && (
            <small>{t('emptyFolder')}</small>
          )}
        </ul>
      </Collapsible>
      <Checkbox
        key={currState}
        checked={checked}
        disabled={disabled}
        className={classNames(nodeClass, 'abs-top')}
        onChange={() => onChange(node?._id, currState)}
      />
    </div>
  );
};

export default FolderNodeCollaps;
